import type { Rect } from "@/lib/math/rect";

export type HorizontalConstraint = "left" | "right" | "leftRight" | "center" | "scale";
export type VerticalConstraint = "top" | "bottom" | "topBottom" | "center" | "scale";

export interface NodeConstraints {
  horizontal: HorizontalConstraint;
  vertical: VerticalConstraint;
}

export const DEFAULT_NODE_CONSTRAINTS: NodeConstraints = {
  horizontal: "left",
  vertical: "top",
};

const MIN_CONSTRAINT_SIZE = 0;

export function normalizeNodeConstraints(value: Partial<NodeConstraints> | undefined): NodeConstraints {
  return {
    horizontal: isHorizontalConstraint(value?.horizontal) ? value.horizontal : DEFAULT_NODE_CONSTRAINTS.horizontal,
    vertical: isVerticalConstraint(value?.vertical) ? value.vertical : DEFAULT_NODE_CONSTRAINTS.vertical,
  };
}

function isHorizontalConstraint(value: unknown): value is HorizontalConstraint {
  return value === "left" || value === "right" || value === "leftRight" || value === "center" || value === "scale";
}

function isVerticalConstraint(value: unknown): value is VerticalConstraint {
  return value === "top" || value === "bottom" || value === "topBottom" || value === "center" || value === "scale";
}

export function horizontalConstraintLabel(constraint: HorizontalConstraint): string {
  switch (constraint) {
    case "left":
      return "Left";
    case "right":
      return "Right";
    case "leftRight":
      return "L + R";
    case "center":
      return "Center";
    case "scale":
      return "Scale";
  }
}

export function verticalConstraintLabel(constraint: VerticalConstraint): string {
  switch (constraint) {
    case "top":
      return "Top";
    case "bottom":
      return "Bottom";
    case "topBottom":
      return "T + B";
    case "center":
      return "Center";
    case "scale":
      return "Scale";
  }
}

export function applyConstraintsToBounds(
  childBounds: Rect,
  fromParent: Rect,
  toParent: Rect,
  constraints: NodeConstraints,
): Rect {
  const fromLeft = childBounds.x - fromParent.x;
  const fromTop = childBounds.y - fromParent.y;
  const fromRight = (fromParent.x + fromParent.width) - (childBounds.x + childBounds.width);
  const fromBottom = (fromParent.y + fromParent.height) - (childBounds.y + childBounds.height);

  let x = childBounds.x;
  let y = childBounds.y;
  let width = childBounds.width;
  let height = childBounds.height;

  switch (constraints.horizontal) {
    case "left":
      x = toParent.x + fromLeft;
      break;
    case "right":
      x = toParent.x + toParent.width - width - fromRight;
      break;
    case "leftRight":
      x = toParent.x + fromLeft;
      width = Math.max(MIN_CONSTRAINT_SIZE, toParent.width - fromLeft - fromRight);
      break;
    case "center": {
      const relativeCenter = fromParent.width === 0
        ? 0.5
        : (fromLeft + width / 2) / fromParent.width;
      x = toParent.x + relativeCenter * toParent.width - width / 2;
      break;
    }
    case "scale": {
      const scaleX = fromParent.width === 0 ? 1 : toParent.width / fromParent.width;
      x = toParent.x + fromLeft * scaleX;
      width = Math.max(MIN_CONSTRAINT_SIZE, width * scaleX);
      break;
    }
  }

  switch (constraints.vertical) {
    case "top":
      y = toParent.y + fromTop;
      break;
    case "bottom":
      y = toParent.y + toParent.height - height - fromBottom;
      break;
    case "topBottom":
      y = toParent.y + fromTop;
      height = Math.max(MIN_CONSTRAINT_SIZE, toParent.height - fromTop - fromBottom);
      break;
    case "center": {
      const relativeCenter = fromParent.height === 0
        ? 0.5
        : (fromTop + height / 2) / fromParent.height;
      y = toParent.y + relativeCenter * toParent.height - height / 2;
      break;
    }
    case "scale": {
      const scaleY = fromParent.height === 0 ? 1 : toParent.height / fromParent.height;
      y = toParent.y + fromTop * scaleY;
      height = Math.max(MIN_CONSTRAINT_SIZE, height * scaleY);
      break;
    }
  }

  return { x, y, width, height };
}

export function constraintsFromHorizontalPin(side: "left" | "right", current: NodeConstraints): NodeConstraints {
  if (side === "left") {
    if (current.horizontal === "right") {
      return { ...current, horizontal: "leftRight" };
    }
    if (current.horizontal === "leftRight") {
      return { ...current, horizontal: "right" };
    }
    return { ...current, horizontal: "left" };
  }

  if (current.horizontal === "left") {
    return { ...current, horizontal: "leftRight" };
  }
  if (current.horizontal === "leftRight") {
    return { ...current, horizontal: "left" };
  }
  return { ...current, horizontal: "right" };
}

export function constraintsFromVerticalPin(side: "top" | "bottom", current: NodeConstraints): NodeConstraints {
  if (side === "top") {
    if (current.vertical === "bottom") {
      return { ...current, vertical: "topBottom" };
    }
    if (current.vertical === "topBottom") {
      return { ...current, vertical: "bottom" };
    }
    return { ...current, vertical: "top" };
  }

  if (current.vertical === "top") {
    return { ...current, vertical: "topBottom" };
  }
  if (current.vertical === "topBottom") {
    return { ...current, vertical: "top" };
  }
  return { ...current, vertical: "bottom" };
}

export function horizontalPinActive(constraint: HorizontalConstraint, side: "left" | "right"): boolean {
  return constraint === "leftRight" || (side === "left" ? constraint === "left" : constraint === "right");
}

export function verticalPinActive(constraint: VerticalConstraint, side: "top" | "bottom"): boolean {
  return constraint === "topBottom" || (side === "top" ? constraint === "top" : constraint === "bottom");
}
